import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { PaginationMeta } from '@/features/chatbots/types/whatsappTemplates.types';

interface PaginationProps {
  pagination: PaginationMeta;
  onPageChange: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
}

const defaultPageSizeOptions = [10, 20, 50];

// Custom PageSize Dropdown Component
function PageSizeDropdown({ value, options = defaultPageSizeOptions, onChange }: { value: number; options?: number[]; onChange: (size: number) => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (size: number) => {
    onChange(size);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                   rounded-sm hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2 
                   focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-700 dark:text-gray-300"
      >
        <span className="font-medium">{value}</span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute bottom-full mb-1 z-100 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                    rounded-md shadow-lg min-w-[80px] overflow-hidden animate-in fade-in slide-in-from-bottom-1 duration-200">
          {options.map((size) => (
            <button
              key={size}
              type="button"
              onClick={() => handleSelect(size)}
              className={`w-full px-3 py-1.5 text-xs text-left transition-colors duration-150 ${size === value
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
            >
              {size}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Pagination({
  pagination,
  onPageChange,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = defaultPageSizeOptions,
}: PaginationProps) {
  // Ensure all values are numbers to avoid string concatenation bugs (e.g. "1" + 1 = "11")
  const page = Number(pagination.page);
  const totalPages = Number(pagination.totalPages);
  const total = Number(pagination.total);
  
  const hasNextPage = pagination.hasNextPage ?? (page < totalPages);
  const hasPrevPage = pagination.hasPrevPage ?? (page > 1);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (page > 3) {
        pages.push('...');
      }

      // Show pages around current page
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (page < totalPages - 2) {
        pages.push('...');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();
  const effectivePageSize = pageSize || pagination.perPage || 10;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
      {/* Info */}
      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-300">
        <span>
          Showing <span className="font-medium text-gray-900 dark:text-white">{((page - 1) * effectivePageSize) + 1}</span>
          {' - '}
          <span className="font-medium text-gray-900 dark:text-white">{Math.min(page * effectivePageSize, total)}</span>
          {' of '}
          <span className="font-medium text-gray-900 dark:text-white">{total}</span>
        </span>

        {/* Page Size Selector */}
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-xs">Show:</span>
            <PageSizeDropdown
              value={effectivePageSize}
              options={pageSizeOptions}
              onChange={onPageSizeChange}
            />
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-1">
        {/* First Page */}
        <NavButton
          onClick={() => onPageChange(1)}
          disabled={!hasPrevPage}
          icon={ChevronsLeft}
          tooltip="First Page"
        />

        {/* Previous Page */}
        <NavButton
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrevPage}
          icon={ChevronLeft}
          tooltip="Previous Page"
        />

        {/* Page Numbers */}
        <div className="flex items-center gap-1 mx-1">
          {pageNumbers.map((pageNum, idx) => (
            <PageNumber
              key={idx}
              page={pageNum}
              isActive={pageNum === page}
              onClick={() => typeof pageNum === 'number' && onPageChange(pageNum)}
            />
          ))}
        </div>

        {/* Next Page */}
        <NavButton
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNextPage}
          icon={ChevronRight}
          tooltip="Next Page"
        />

        {/* Last Page */}
        <NavButton
          onClick={() => onPageChange(totalPages)}
          disabled={!hasNextPage}
          icon={ChevronsRight}
          tooltip="Last Page"
        />
      </div>
    </div>
  );
}

// Navigation Button
interface NavButtonProps {
  onClick: () => void;
  disabled: boolean;
  icon: React.ElementType;
  tooltip?: string;
}

function NavButton({ onClick, disabled, icon: Icon, tooltip }: NavButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={tooltip}
      className="p-2 text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-300
                 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors
                 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent
                 disabled:hover:text-gray-500"
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

// Page Number Button
interface PageNumberProps {
  page: number | string;
  isActive: boolean;
  onClick: () => void;
}

function PageNumber({ page, isActive, onClick }: PageNumberProps) {
  if (page === '...') {
    return (
      <span className="px-3 py-1.5 text-sm text-gray-400 dark:text-gray-400">
        ...
      </span>
    );
  }

  return (
    <button
      onClick={onClick}
      className={`min-w-[36px] h-9 px-3 rounded-sm text-sm font-medium transition-all duration-200
                  ${isActive
          ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25'
          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
        }`}
    >
      {page}
    </button>
  );
}
